/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ClassNames } from '@emotion/react'
import type { FC } from 'react'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { Transition as TransitionComponent } from 'react-transition-group'
import type { TransitionStatus } from 'react-transition-group/Transition'

import type { CollapseProps } from './types'
import useForkRef from '../../../hooks/use-fork-ref'
import type { Theme } from '../../../theme-provider/types'
import { useSafeThemeUI } from '../../../theme-provider/use-theme-ui'
import { duration } from '../../../themes/theme-keys/transitions'
import Box from '../../_primitives/box/box'
import Flex from '../../_primitives/flex/flex'
import { getTransitionProps } from '../transitions/utils'

const styles = (orientation: 'horizontal' | 'vertical', theme?: Theme) => {
  const isHorizontal = orientation === 'horizontal'
  const size = isHorizontal ? 'width' : 'height'
  return {
    container: {
      [size]: '0',
      overflow: 'hidden',
      transition: theme?.transitions ? theme.transitions.create(size) : size
    }
  }
}

/**
 * The Collapse transition is used by the
 * [Vertical Stepper](/components/steppers/#vertical-stepper) StepContent component.
 * It uses [react-transition-group](https://github.com/reactjs/react-transition-group) internally.
 */
const Collapse: FC<CollapseProps> = React.forwardRef<
  HTMLElement,
  CollapseProps & { theme?: Theme }
>(function Collapse(props, ref) {
  const {
    children,
    collapsedSize: collapsedSizeProp = '0px',
    component: Component = Box,
    easing,
    in: inProp,
    onEnter,
    onEntered,
    onEntering,
    onExit,
    onExited,
    onExiting,
    addEndListener,
    style,
    timeout = duration.standard,
    orientation = 'vertical',
    wrapperProps,
    ...other
  } = props
  const theme = useSafeThemeUI(props)
  const timer = useRef<any>()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const autoTransitionDuration = useRef<number>()
  const isHorizontal = orientation === 'horizontal'
  const size = isHorizontal ? 'width' : 'height'
  const collapsedSize =
    typeof collapsedSizeProp === 'number'
      ? `${collapsedSizeProp}px`
      : collapsedSizeProp

  useEffect(() => {
    return () => {
      clearTimeout(timer.current)
    }
  }, [])

  const nodeRef = useRef<HTMLElement>(null)
  const handleRef = useForkRef(ref, nodeRef as any)

  const normalizedTransitionCallback =
    (callback: (node: HTMLElement, maybeIsAppearing?: boolean) => void) =>
    (maybeIsAppearing?: boolean) => {
      if (callback) {
        const node = nodeRef.current as HTMLElement

        // onEnterXxx and onExitXxx callbacks have a different arguments.length value.
        if (maybeIsAppearing === undefined) {
          callback(node)
        } else {
          callback(node, maybeIsAppearing)
        }
      }
    }

  const getWrapperSize = () =>
    wrapperRef.current
      ? wrapperRef.current[isHorizontal ? 'clientWidth' : 'clientHeight']
      : 0

  const handleEnter = normalizedTransitionCallback((node, isAppearing) => {
    if (wrapperRef.current && isHorizontal) {
      // Set absolute position to get the size of collapsed content
      wrapperRef.current.style.position = 'absolute'
    }
    node.style[size] = collapsedSize

    if (onEnter) {
      onEnter(node, !!isAppearing)
    }
  })

  const handleEntering = normalizedTransitionCallback(
    (node: HTMLElement, isAppearing?: boolean) => {
      const wrapperSize = getWrapperSize()
      // eslint-disable-next-line no-console
      // console.log(`handleEntering wrapperSize ${wrapperSize}`)

      if (wrapperRef.current && isHorizontal) {
        // After the size is read reset the position back to default
        wrapperRef.current.style.position = ''
      }

      const { duration: transitionDuration } = getTransitionProps(
        {
          style,
          timeout: timeout !== 'auto' ? timeout : duration.standard,
          easing
        },
        {
          mode: 'enter'
        }
      )

      if (timeout === 'auto') {
        const duration2 = theme.transitions
          ? theme.transitions.getAutoHeightDuration(wrapperSize)
          : 500
        node.style.transitionDuration = `${duration2}ms`
        autoTransitionDuration.current = duration2
      } else {
        node.style.transitionDuration =
          typeof transitionDuration === 'string'
            ? transitionDuration
            : `${transitionDuration}ms`
      }

      node.style[size] = `${wrapperSize}px`

      if (onEntering) {
        onEntering(node, !!isAppearing)
      }
    }
  )

  const handleEntered = normalizedTransitionCallback(
    (node: HTMLElement, isAppearing?: boolean) => {
      node.style[size] = 'auto'
      // eslint-disable-next-line no-console
      // console.log(`handleEntered`)
      if (onEntered) {
        onEntered(node, !!isAppearing)
      }
    }
  )

  const handleExit = normalizedTransitionCallback((node: HTMLElement) => {
    node.style[size] = `${getWrapperSize()}px`

    if (onExit) {
      onExit(node)
    }
  })

  const handleExited = normalizedTransitionCallback((node: HTMLElement) => {
    if (onExited) {
      onExited(node)
    }
  })

  const handleExiting = normalizedTransitionCallback((node: HTMLElement) => {
    const wrapperSize = wrapperRef.current
      ? wrapperRef.current[isHorizontal ? 'clientWidth' : 'clientHeight']
      : 0
    // eslint-disable-next-line no-console
    // console.log(`handleExiting wrapperSize ${wrapperSize}`)
    const { duration: transitionDuration } = getTransitionProps(
      {
        style,
        timeout: timeout !== 'auto' ? timeout : duration.standard,
        easing
      },
      {
        mode: 'exit'
      }
    )

    if (timeout === 'auto') {
      const duration2 = theme.transitions
        ? theme.transitions.getAutoHeightDuration(wrapperSize)
        : 500
      node.style.transitionDuration = `${duration2}ms`
      autoTransitionDuration.current = duration2
    } else {
      node.style.transitionDuration =
        typeof transitionDuration === 'string'
          ? transitionDuration
          : `${transitionDuration}ms`
    }

    node.style[size] = collapsedSize

    if (onExiting) {
      onExiting(node)
    }
  })

  const handleAddEndListener = useCallback(
    (next: () => void) => {
      if (timeout === 'auto') {
        timer.current = setTimeout(next, autoTransitionDuration.current || 0)
      }
      if (addEndListener && nodeRef.current) {
        // Old call signature before `react-transition-group` implemented `nodeRef`
        addEndListener(nodeRef.current, next)
      }
    },
    [addEndListener, timeout]
  )

  const _styles = useMemo(
    () => styles(orientation, theme),
    [theme, orientation]
  )
  return (
    <TransitionComponent
      in={inProp}
      onEnter={handleEnter}
      onEntered={handleEntered}
      onEntering={handleEntering}
      onExit={handleExit}
      onExited={handleExited}
      onExiting={handleExiting}
      addEndListener={handleAddEndListener}
      nodeRef={nodeRef}
      timeout={timeout === 'auto' ? 500 : timeout}
      {...other}
    >
      {
        // @ts-ignore
        (state: TransitionStatus, childProps: any) => {
          const componentProps: any = {
            ref: handleRef,
            __css: _styles.container,
            [isHorizontal ? 'minWidth' : 'minHeight']: collapsedSize,
            ...childProps
          }
          const exited =
            state === 'exited' && !inProp && collapsedSize === '0px'
          return (
            <ClassNames>
              {({ css }) => (
                <Component
                  className={
                    state === 'entered'
                      ? isHorizontal
                        ? css`
                            width: auto;
                            overflow: visible;
                          `
                        : css`
                            height: auto;
                            overflow: visible;
                          `
                      : exited
                        ? css`
                            visibility: hidden;
                          `
                        : undefined
                  }
                  {...componentProps}
                >
                  <Flex ref={wrapperRef} className="cs-tui-collapse-wrapper">
                    {isHorizontal ? (
                      <Box
                        className="cs-tui-collapse-wrapper-inner"
                        width="auto"
                        height="100%"
                        {...wrapperProps}
                      >
                        {children}
                      </Box>
                    ) : (
                      <Box
                        className="cs-tui-collapse-wrapper-inner"
                        width="100%"
                        {...wrapperProps}
                      >
                        {children}
                      </Box>
                    )}
                  </Flex>
                </Component>
              )}
            </ClassNames>
          )
        }
      }
    </TransitionComponent>
  )
})

export default Collapse
